import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Check, Share2, Clock, Package, Truck, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import PixCheckout from '../components/PixCheckout';
import api, { formatCurrency, formatDate, statusLabels, paymentMethodLabels } from '../lib/api';
import { toast } from 'sonner';

const OrderConfirmationPage = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifying, setNotifying] = useState(false);

  useEffect(() => {
    const loadOrder = async () => {
      try {
        const response = await api.get(`/orders/${id}`);
        setOrder(response.data);
      } catch (error) {
        toast.error('Pedido não encontrado');
      } finally {
        setLoading(false);
      }
    };
    loadOrder();
  }, [id]);

  const handleAlreadyPaid = async () => {
    if (notifying) return;
    setNotifying(true);
    try {
      await api.post(`/orders/${order.id}/notify-payment`);
      toast.success('Pagamento informado! Confirmaremos em breve.');
      const response = await api.get(`/orders/${id}`);
      setOrder(response.data);
    } catch (e) {
      toast.error('Não foi possível notificar agora. Tente novamente.');
    } finally {
      setNotifying(false);
    }
  };

  const shareOnWhatsApp = () => {
    const text = `🛒 *Pedido KITS TOP*\n\nNº do Pedido: ${order.order_number}\nValor: ${formatCurrency(order.total)}\nStatus: ${statusLabels[order.status]}\n\nObrigado pela preferência!`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank');
  };

  const getStatusStep = () => {
    switch (order?.status) {
      case 'pending': return 0;
      case 'paid': return 1;
      case 'preparing': return 2;
      case 'shipping': return 3;
      case 'delivered': return 4;
      default: return 0;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto space-y-6">
            <Skeleton className="h-40 rounded-2xl" />
            <Skeleton className="h-60 rounded-2xl" />
            <Skeleton className="h-40 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="container mx-auto px-4 py-8 text-center">
          <h2 className="font-heading text-xl font-bold">Pedido não encontrado</h2>
          <Link to="/">
            <Button className="mt-4">Voltar ao início</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-4 bg-success/10 rounded-full flex items-center justify-center">
              <Check className="w-10 h-10 text-success" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground mb-2">
              Pedido Confirmado!
            </h1>
            <p className="text-muted-foreground">
              Nº do Pedido: <span className="font-bold text-foreground" data-testid="order-number">{order.order_number}</span>
            </p>
          </div>

          {/* Order Status */}
          <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
            <h2 className="font-heading text-lg font-bold text-foreground mb-4">
              Status do Pedido
            </h2>
            
            <div className="relative">
              <div className="flex justify-between mb-2">
                {['Pendente', 'Pago', 'Preparando', 'Enviando', 'Entregue'].map((step, index) => {
                  const currentStep = getStatusStep();
                  const isCompleted = index <= currentStep;
                  const isCurrent = index === currentStep;
                  
                  return (
                    <div key={step} className="flex flex-col items-center flex-1">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 ${
                          isCompleted ? 'bg-success text-white' : 'bg-slate-200 text-slate-400'
                        } ${isCurrent ? 'ring-4 ring-success/20' : ''}`}
                      >
                        {isCompleted ? (
                          <Check className="w-4 h-4" />
                        ) : index === 0 ? (
                          <Clock className="w-4 h-4" />
                        ) : index === 2 ? (
                          <Package className="w-4 h-4" />
                        ) : index === 3 ? (
                          <Truck className="w-4 h-4" />
                        ) : (
                          <CheckCircle className="w-4 h-4" />
                        )}
                      </div>
                      <span className={`text-xs text-center ${isCompleted ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                        {step}
                      </span>
                    </div>
                  );
                })}
              </div>
              
              <div className="absolute top-4 left-0 right-0 h-0.5 bg-slate-200 -z-10">
                <div
                  className="h-full bg-success transition-all duration-500"
                  style={{ width: `${(getStatusStep() / 4) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* PIX Payment - QR Code Real (EMV-BCB) */}
          {order.payment_method === 'pix' && order.payment_status === 'pending' && (
            <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
              <h2 className="font-heading text-lg font-bold text-foreground mb-4">
                Pagamento via PIX
              </h2>
              <PixCheckout
                amount={order.total}
                txid={order.order_number || order.id}
                description={`Pedido ${order.order_number || ''}`.trim()}
                onAlreadyPaid={handleAlreadyPaid}
              />
            </div>
          )}

          {/* Pagamento informado, aguardando confirmação */}
          {order.payment_method === 'pix' && order.payment_status === 'awaiting_confirmation' && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6 text-center" data-testid="payment-awaiting-confirmation">
              <Clock className="w-10 h-10 mx-auto text-amber-600 mb-2" />
              <h3 className="font-heading text-lg font-bold text-amber-900 mb-1">
                Pagamento informado!
              </h3>
              <p className="text-sm text-amber-800">
                Estamos confirmando seu pagamento. Você receberá atualização em instantes.
              </p>
            </div>
          )}

          {/* Order Details */}
          <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
            <h2 className="font-heading text-lg font-bold text-foreground mb-4">
              Detalhes do Pedido
            </h2>
            
            <div className="space-y-4">
              {order.items.map((item, index) => (
                <div key={index} className="flex justify-between items-center py-2 border-b last:border-0">
                  <div>
                    <p className="font-medium">{item.product_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {item.quantity}x {formatCurrency(item.product_price)}
                    </p>
                  </div>
                  <p className="font-medium">{formatCurrency(item.subtotal)}</p>
                </div>
              ))}
              
              <div className="flex justify-between pt-4 border-t">
                <span className="font-bold">Total</span>
                <span className="font-bold text-xl text-primary" data-testid="order-total">
                  {formatCurrency(order.total)}
                </span>
              </div>
            </div>
          </div>

          {/* Customer & Delivery Info */}
          <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
            <h2 className="font-heading text-lg font-bold text-foreground mb-4">
              Informações de Entrega
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Nome</p>
                <p className="font-medium">{order.customer.name}</p>
              </div>
              <div>
                <p className="text-muted-foreground">CPF</p>
                <p className="font-medium">{order.customer.cpf}</p>
              </div>
              <div>
                <p className="text-muted-foreground">WhatsApp</p>
                <p className="font-medium">{order.customer.whatsapp}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Forma de Pagamento</p>
                <p className="font-medium">{paymentMethodLabels[order.payment_method]}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-muted-foreground">Endereço de Entrega</p>
                <p className="font-medium">
                  {order.customer.address}, {order.customer.city} - {order.customer.state}, {order.customer.zip_code}
                </p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-muted-foreground">Data do Pedido</p>
                <p className="font-medium">{formatDate(order.created_at)}</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              variant="outline"
              className="flex-1 rounded-xl"
              onClick={shareOnWhatsApp}
              data-testid="share-order-button"
            >
              <Share2 className="mr-2 h-4 w-4" />
              Compartilhar
            </Button>
            <Link to="/meus-pedidos" className="flex-1">
              <Button variant="outline" className="w-full rounded-xl" data-testid="my-orders-button">
                Meus Pedidos
              </Button>
            </Link>
            <Link to="/" className="flex-1">
              <Button className="w-full rounded-xl shadow-primary" data-testid="continue-shopping-button">
                Continuar Comprando
              </Button>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OrderConfirmationPage;
